import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TableSortLabel,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';
import type { ItemExchangeRate } from '../../services/marketAnalysisService';

// Define table column types
type ColumnId = 'name' | 'exaltedRate' | 'chaosRate' | 'divineRate' | 'lastUpdated';

type Column = {
  id: ColumnId;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: number) => string;
};

const columns: Column[] = [
  { id: 'name', label: 'Item Name', minWidth: 170 },
  {
    id: 'exaltedRate',
    label: 'Exalted Price',
    minWidth: 100,
    align: 'right',
    format: (value: number) => value.toFixed(4),
  },
  {
    id: 'chaosRate',
    label: 'Chaos Price',
    minWidth: 100,
    align: 'right',
    format: (value: number) => value.toFixed(2),
  },
  {
    id: 'divineRate',
    label: 'Divine Price',
    minWidth: 100,
    align: 'right',
    format: (value: number) => value.toFixed(4),
  },
  {
    id: 'lastUpdated',
    label: 'Last Updated',
    minWidth: 170,
    align: 'right',
  },
];

// Define props interface
interface ExchangeRatesTableProps {
  exchangeRates: ItemExchangeRate[];
  exaltedOrbId: string | null;
  chaosOrbId: string | null;
  divineOrbId: string | null;
  isLoading: boolean;
  onExchangeRateClick: (itemId: string, currencyId: string) => void;
  onResetMarketData?: () => void;
  isResetting?: boolean;
  gameId?: string;
  leagueId?: string;
}

const ExchangeRatesTable: React.FC<ExchangeRatesTableProps> = ({
  exchangeRates,
  exaltedOrbId,
  chaosOrbId,
  divineOrbId,
  isLoading,
  onExchangeRateClick,
  onResetMarketData,
  isResetting = false,
  gameId,
  leagueId,
}) => {
  // Get user to check if admin
  const user = useSelector(selectUser);
  const isAdmin = user?.roles?.includes('Admin') || false;

  // State for filtered/sorted data
  const [filteredRates, setFilteredRates] = useState<Array<ItemExchangeRate & { name: string }>>([]);
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  // Sort state
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<ColumnId>('exaltedRate');
  // State for reset confirmation dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  // Process exchange rates to include item names
  useEffect(() => {
    if (!exchangeRates.length) {
      setFilteredRates([]);
      return;
    }

    // Include all items with proper itemFrom and itemTo relationships
    // Removed the filter that was excluding currency items
    const relevantRates = exchangeRates.filter(rate => {
      return rate.itemFrom && rate.itemTo;
    });

    // Group rates by itemFromId (the actual item) to prevent duplication
    const groupedByItem = new Map<string, ItemExchangeRate & { name: string }>();

    // Process each rate to build our grouped items
    relevantRates.forEach(rate => {
      const itemId = rate.itemFromId;
      const itemName = rate.itemFrom?.item_name || 'Unknown Item';

      // If we haven't seen this item yet, add it to our map
      if (!groupedByItem.has(itemId)) {
        groupedByItem.set(itemId, {
          ...rate,
          name: itemName,
        });
      }
    });

    // Convert the map to an array
    const uniqueRatesWithNames = Array.from(groupedByItem.values());

    // Apply filtering
    const filtered = uniqueRatesWithNames.filter(rate => rate.name.toLowerCase().includes(searchQuery.toLowerCase()));

    // Apply sorting
    const sorted = sortData(filtered);
    setFilteredRates(sorted);
  }, [exchangeRates, searchQuery, order, orderBy, exaltedOrbId, chaosOrbId, divineOrbId]);

  // Sorting logic
  type SortOrder = 'asc' | 'desc';

  // Custom comparator for table data
  const getComparator = (
    order: SortOrder,
    orderBy: ColumnId,
  ): ((a: ItemExchangeRate & { name: string }, b: ItemExchangeRate & { name: string }) => number) => {
    return order === 'desc'
      ? (a, b) => {
          // Handle different column types
          if (orderBy === 'name') {
            return b.name.localeCompare(a.name);
          } else if (orderBy === 'lastUpdated') {
            return new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime();
          } else {
            // For rate columns, we need to find the appropriate rate
            const currencyId =
              orderBy === 'exaltedRate'
                ? exaltedOrbId
                : orderBy === 'chaosRate'
                  ? chaosOrbId
                  : orderBy === 'divineRate'
                    ? divineOrbId
                    : null;

            if (!currencyId) return 0;

            const aRate = findExchangeRate(exchangeRates, a.itemFromId, currencyId);
            const bRate = findExchangeRate(exchangeRates, b.itemFromId, currencyId);

            if (aRate === null && bRate === null) return 0;
            if (aRate === null) return 1;
            if (bRate === null) return -1;

            return bRate - aRate;
          }
        }
      : (a, b) => {
          // Same logic but reversed for ascending order
          if (orderBy === 'name') {
            return a.name.localeCompare(b.name);
          } else if (orderBy === 'lastUpdated') {
            return new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime();
          } else {
            const currencyId =
              orderBy === 'exaltedRate'
                ? exaltedOrbId
                : orderBy === 'chaosRate'
                  ? chaosOrbId
                  : orderBy === 'divineRate'
                    ? divineOrbId
                    : null;

            if (!currencyId) return 0;

            const aRate = findExchangeRate(exchangeRates, a.itemFromId, currencyId);
            const bRate = findExchangeRate(exchangeRates, b.itemFromId, currencyId);

            if (aRate === null && bRate === null) return 0;
            if (aRate === null) return 1;
            if (bRate === null) return -1;

            return aRate - bRate;
          }
        };
  };

  // Sort function
  const sortData = (data: Array<ItemExchangeRate & { name: string }>) => {
    return [...data].sort(getComparator(order, orderBy));
  };

  // Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleRequestSort = (property: ColumnId) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Helper to format dates
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy HH:mm');
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Helper to find exchange rate for a specific currency
  const findExchangeRate = (rates: ItemExchangeRate[], itemId: string, currencyId: string | null) => {
    if (!currencyId) return null;

    const rate = rates.find(r => r.itemFromId === itemId && r.itemToId === currencyId);

    if (rate) {
      return rate.exchangeRate || rate.quantityTo / rate.quantityFrom;
    }
    return null;
  };

  // Handlers for reset dialog
  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
  };

  const handleConfirmReset = () => {
    if (onResetMarketData) {
      onResetMarketData();
    }
    setResetDialogOpen(false);
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          sx={{ flexGrow: 1, mr: isAdmin ? 2 : 0 }}
          placeholder='Search by item name...'
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Admin Reset Button */}
        {isAdmin && (
          <Button
            variant='outlined'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={handleOpenResetDialog}
            disabled={isResetting || !gameId || !leagueId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {isResetting ? 'Resetting...' : 'Reset Market Data'}
          </Button>
        )}
      </Box>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog}>
        <DialogTitle>Reset Market Analysis Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all item exchange rate data for the currently selected game and league.
            <br />
            <br />
            <strong>Game:</strong> {gameId || 'None'}
            <br />
            <strong>League:</strong> {leagueId || 'None'}
            <br />
            <br />
            This action cannot be undone. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>Cancel</Button>
          <Button onClick={handleConfirmReset} color='error' variant='contained'>
            Reset Data
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer sx={{ maxHeight: 640 }}>
        <Table stickyHeader aria-label='exchange rates table'>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                  {column.id === 'name' || column.id === 'lastUpdated' ? (
                    column.label
                  ) : (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align='center'>
                  <CircularProgress sx={{ my: 2 }} />
                  <Typography>Loading exchange rates...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredRates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align='center'>
                  <Typography>{searchQuery ? 'No items match your search criteria' : 'No exchange rates available'}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRates.map(rate => {
                // Find rates for each currency
                const exaltedRate = findExchangeRate(exchangeRates, rate.itemFromId, exaltedOrbId);
                const chaosRate = findExchangeRate(exchangeRates, rate.itemFromId, chaosOrbId);
                const divineRate = findExchangeRate(exchangeRates, rate.itemFromId, divineOrbId);

                return (
                  <TableRow hover tabIndex={-1} key={rate.id}>
                    <TableCell>{rate.name}</TableCell>
                    <TableCell
                      align='right'
                      onClick={() => {
                        if (exaltedOrbId && rate.itemFromId !== exaltedOrbId) {
                          onExchangeRateClick(rate.itemFromId, exaltedOrbId);
                        }
                      }}
                      sx={{
                        cursor: exaltedOrbId && rate.itemFromId !== exaltedOrbId ? 'pointer' : 'default',
                        '&:hover':
                          exaltedOrbId && rate.itemFromId !== exaltedOrbId ? { textDecoration: 'underline', color: 'primary.main' } : {},
                      }}
                    >
                      {exaltedRate !== null ? exaltedRate.toFixed(4) : 'N/A'}
                    </TableCell>
                    <TableCell
                      align='right'
                      onClick={() => {
                        if (chaosOrbId && rate.itemFromId !== chaosOrbId) {
                          onExchangeRateClick(rate.itemFromId, chaosOrbId);
                        }
                      }}
                      sx={{
                        cursor: chaosOrbId && rate.itemFromId !== chaosOrbId ? 'pointer' : 'default',
                        '&:hover':
                          chaosOrbId && rate.itemFromId !== chaosOrbId ? { textDecoration: 'underline', color: 'primary.main' } : {},
                      }}
                    >
                      {chaosRate !== null ? chaosRate.toFixed(2) : 'N/A'}
                    </TableCell>
                    <TableCell
                      align='right'
                      onClick={() => {
                        if (divineOrbId && rate.itemFromId !== divineOrbId) {
                          onExchangeRateClick(rate.itemFromId, divineOrbId);
                        }
                      }}
                      sx={{
                        cursor: divineOrbId && rate.itemFromId !== divineOrbId ? 'pointer' : 'default',
                        '&:hover':
                          divineOrbId && rate.itemFromId !== divineOrbId ? { textDecoration: 'underline', color: 'primary.main' } : {},
                      }}
                    >
                      {divineRate !== null ? divineRate.toFixed(4) : 'N/A'}
                    </TableCell>
                    <TableCell align='right'>
                      {formatDate(rate.recordedAt)}
                      <Chip size='small' label='Click rate for history' variant='outlined' sx={{ ml: 1, height: 20, fontSize: '0.6rem' }} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ExchangeRatesTable;
